/**
 * Portal público de cotización.
 * Ruta: /portal/cotizacion?t=TOKEN
 */
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PictureAsPdf } from '@mui/icons-material';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { API_URL, BASE_URL } from '../config';
import { formatAmount } from '../utils/currency';

interface Detalle {
  item_nombre: string;
  item_codigo?: string | null;
  precio: number;
  cantidad: number;
}

interface CotizacionPublica {
  numero_cotizacion: string;
  paciente_nombre?: string;
  vigencia_hasta?: string | null;
  subtotal: number;
  descuento: number;
  total: number;
  estado: string;
  observaciones?: string | null;
  detalles: Detalle[];
  laboratorio?: {
    nombre?: string;
    logo_url?: string | null;
  };
}

function resolverLogoUrl(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (raw.startsWith('http') || raw.startsWith('data:')) return raw;
  return `${BASE_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

const PortalCotizacion: React.FC = () => {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [data, setData] = useState<CotizacionPublica | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [descargando, setDescargando] = useState(false);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Enlace inválido: falta el token.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/public/cotizacion/${encodeURIComponent(token)}`);
        if (!cancelled) {
          setData(res.data);
          setLogoError(false);
        }
      } catch (e: any) {
        if (!cancelled) {
          setError(e?.response?.data?.error || 'No se pudo cargar la cotización.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const descargarPdf = async () => {
    if (!token) return;
    setDescargando(true);
    try {
      const res = await axios.get(`${API_URL}/public/cotizacion/${encodeURIComponent(token)}/pdf`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `cotizacion_${data?.numero_cotizacion || 'documento'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      alert(e?.response?.data?.error || 'No se pudo descargar el PDF.');
    } finally {
      setDescargando(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Alert severity="error">{error || 'Cotización no disponible'}</Alert>
      </Container>
    );
  }

  const nombreLab = (data.laboratorio?.nombre || '').trim();
  const logoUrl = resolverLogoUrl(data.laboratorio?.logo_url);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            mb: 3,
          }}
        >
          {logoUrl && !logoError ? (
            <Box
              component="img"
              src={logoUrl}
              alt={nombreLab || 'Logo laboratorio'}
              onError={() => setLogoError(true)}
              sx={{
                maxWidth: 160,
                maxHeight: 96,
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                mb: 1.5,
              }}
            />
          ) : null}
          {nombreLab ? (
            <Typography variant="h5" fontWeight={700} color="text.primary">
              {nombreLab}
            </Typography>
          ) : null}
        </Box>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Cotización {data.numero_cotizacion}
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          Paciente: {data.paciente_nombre || '—'}
          {data.vigencia_hasta ? ` · Vigencia hasta ${data.vigencia_hasta}` : ''}
        </Typography>
        {data.observaciones && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            {data.observaciones}
          </Typography>
        )}
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Ítem</TableCell>
                <TableCell align="right">Cant.</TableCell>
                <TableCell align="right">Precio</TableCell>
                <TableCell align="right">Importe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.detalles || []).map((d, i) => (
                <TableRow key={i}>
                  <TableCell>{d.item_nombre}</TableCell>
                  <TableCell align="right">{d.cantidad}</TableCell>
                  <TableCell align="right">{formatAmount(d.precio)}</TableCell>
                  <TableCell align="right">
                    {formatAmount(Number(d.precio) * Number(d.cantidad || 1))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ textAlign: 'right', mb: 2 }}>
          <Typography variant="body2">Subtotal: {formatAmount(data.subtotal)}</Typography>
          <Typography variant="body2">Descuento: {formatAmount(data.descuento)}</Typography>
          <Typography variant="h6">Total: {formatAmount(data.total)}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={descargando ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdf />}
          onClick={() => void descargarPdf()}
          disabled={descargando}
        >
          Descargar PDF
        </Button>
      </Paper>
    </Container>
  );
};

export default PortalCotizacion;
